<template>
  <DefaultLayout
    :pageTitle="meta.title"
    :pageDescription="meta.description"
    :showFooterCTA="true"
  >
    <PageHeader
      title="The Transpond<br>Marketing Glossary"
      subtitle-text="Plain-English definitions for the email, SMS, automation and CRM terms you'll run into as you grow."
      class="mb-0 no-submenu"
    >
      <div class="glossary__search-wrap">
        <svg class="glossary__search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
        <input
          class="glossary__search-input"
          type="text"
          v-model="searchQuery"
          placeholder="Search terms &mdash; try &lsquo;deliverability&rsquo; or &lsquo;A/B testing&rsquo;"
        />
      </div>

    </PageHeader>

    <div class="container-fluid">
      <div class="row">
        <div class="col-xs-12">
          <div class="glossary__layout">
            <div class="glossary__az-nav" v-bind:class="{ 'is-shown': pageShown }">
              <span class="glossary__az-label">Jump to</span>
              <div class="glossary__az-grid" v-bind:class="{ 'is-fading': resultsFading }">
                <template v-for="letter in alphabet" v-bind:key="letter">
                  <a
                    v-if="activeLetters.has(letter)"
                    v-bind:href="'#letter-' + letter"
                    class="glossary__az-item glossary__az-item--filled"
                  >{{ letter }}</a>
                  <span v-else class="glossary__az-item">{{ letter }}</span>
                </template>
              </div>

              <div class="glossary__filters">
                <span class="glossary__filters-title">Filter by topic</span>
                <ul class="glossary__filters-list">
                  <li>
                    <button
                      type="button"
                      class="glossary__filters-item"
                      v-bind:class="{ 'glossary__filters-item--active': activeCategory === null }"
                      v-on:click="setCategory(null)"
                    >
                      All terms
                    </button>
                  </li>
                  <li v-for="category in categories" v-bind:key="category">
                    <button
                      type="button"
                      class="glossary__filters-item"
                      v-bind:class="{ 'glossary__filters-item--active': activeCategory === category }"
                      v-on:click="setCategory(category)"
                    >
                      {{ category }}
                    </button>
                  </li>
                </ul>
              </div>
            </div>

            <div
              class="glossary__terms"
              v-bind:class="{ 'is-shown': pageShown && !resultsFading, 'is-live': hasFiltered }"
            >
              <div
                class="glossary__letter-group"
                v-for="group in groupedTerms"
                v-bind:key="group.letter"
                v-bind:id="'letter-' + group.letter"
              >
                <div class="glossary__letter-head">
                  <span class="glossary__letter-badge">{{ group.letter }}</span>
                </div>

                <div class="glossary__term-row" v-for="term in group.terms" v-bind:key="term.term">
                  <a
                    v-if="termHref(term)"
                    class="glossary__term-name"
                    v-bind:href="termHref(term)"
                  >{{ term.term }}</a>
                  <p v-else class="glossary__term-name">{{ term.term }}</p>
                  <p class="glossary__term-def">{{ term.def }}</p>
                </div>
              </div>

              <p class="glossary__empty" v-if="groupedTerms.length === 0">
                No terms match &ldquo;{{ searchQuery }}&rdquo; &mdash; try another search or clear the filter.
              </p>
            </div>

            <div></div>
          </div>
        </div>
      </div>
    </div>
  </DefaultLayout>
</template>

<script>
import DefaultLayout from "@/layouts/DefaultLayout";
import PageHeader from "@/components/Header/PageHeader";

export default {
  name: "MarketingGlossary",
  components: {
    DefaultLayout,
    PageHeader,
  },
  data() {
    return {
      activeCategory: null,
      appliedCategory: null,
      searchQuery: "",
      appliedQuery: "",
      pageShown: false,
      resultsFading: false,
      hasFiltered: false,
      meta: {
        title: "Marketing Glossary",
        description:
          "Plain-English definitions for the email, SMS, automation and CRM terms you'll run into as you grow.",
      },
      categories: [
        "Email Marketing",
        "Automation",
        "Deliverability",
        "Analytics & Reporting",
        "Contacts & Segmentation",
        "Lead Generation",
        "SMS Marketing",
        "Social & Multichannel",
        "CRM & Integrations",
        "Customer Journey",
        "Transactional Email",
        "AI Marketing",
      ],
      terms: [
        { term: "A/B Testing", letter: "A", category: "Analytics & Reporting", def: "Sending two versions of a campaign to a small slice of your audience to see which performs better, then sending the winner to everyone else." },
        { term: "AI Copywriting", letter: "A", category: "AI Marketing", def: "Using AI tools to draft, vary or refine marketing copy — subject lines, body text, CTAs — faster than writing from scratch." },
        { term: "AI Email Marketing", letter: "A", category: "AI Marketing", def: "Applying AI to email marketing tasks like writing copy, predicting send times, or personalising content at scale." },
        { term: "AI Marketing", letter: "A", category: "AI Marketing", def: "Any use of artificial intelligence to plan, create, or optimise marketing activity across channels." },
        { term: "Bounce Rate", letter: "B", category: "Deliverability", def: "The percentage of emails that couldn't be delivered to a recipient's inbox." },
        { term: "Bulk SMS", letter: "B", category: "SMS Marketing", def: "Sending the same text message to a large group of contacts in one go, typically for promotions or announcements." },
        { term: "Click-Through Rate (CTR)", letter: "C", category: "Analytics & Reporting", def: "The percentage of recipients who clicked a link in your email, measured against the number delivered." },
        { term: "Cold Email", letter: "C", category: "Email Marketing", def: "An email sent to someone with no prior relationship to your business, usually to start a conversation or pitch." },
        { term: "Contact Groups", letter: "C", category: "Contacts & Segmentation", def: "A way of organising contacts into named collections, so you can target or manage them together." },
        { term: "Contact Synchronisation", letter: "C", category: "CRM & Integrations", def: "Automatically keeping contact records matched and up to date between Transpond and another connected tool." },
        { term: "CRM (Customer Relationship Management)", letter: "C", category: "CRM & Integrations", def: "Software that stores contact data and interaction history so your team can manage customer relationships at scale." },
        { term: "Customer Engagement", letter: "C", category: "Customer Journey", def: "How actively your customers interact with your brand across emails, SMS, social and your product." },
        { term: "Customer Journey", letter: "C", category: "Customer Journey", def: "The full path a person takes from first hearing about you to becoming a loyal customer." },
        { term: "Customer Onboarding", letter: "C", category: "Customer Journey", def: "The process of welcoming and guiding a new customer through their first experience with your product or service." },
        { term: "Customer Retention", letter: "C", category: "Customer Journey", def: "Strategies aimed at keeping existing customers buying, rather than only chasing new ones." },
        { term: "Drip Campaign", letter: "D", category: "Automation", def: "A set of pre-written emails sent automatically on a schedule, gradually nurturing a contact toward a goal." },
        { term: "Ecommerce Integration", letter: "E", category: "CRM & Integrations", def: "Connecting your online store to Transpond so orders, products and customer data sync automatically." },
        { term: "Email Automation", letter: "E", category: "Automation", def: "Sending emails automatically based on triggers like a signup, purchase, or date, instead of manually." },
        { term: "Email Deliverability", letter: "E", category: "Deliverability", def: "How reliably your emails reach the inbox rather than being filtered to spam or blocked outright." },
        { term: "Email Marketing", letter: "E", category: "Email Marketing", def: "Sending targeted messages to a list of subscribers to build relationships, promote products, or drive traffic." },
        { term: "Email Marketing Metrics", letter: "E", category: "Analytics & Reporting", def: "The core numbers used to judge a campaign's performance, such as opens, clicks, bounces and unsubscribes." },
        { term: "Email Open Rate", letter: "E", category: "Analytics & Reporting", def: "The percentage of delivered emails that were opened." },
        { term: "Email Segmentation", letter: "E", category: "Contacts & Segmentation", def: "Dividing your list into smaller groups based on shared traits, so you can send more relevant campaigns." },
        { term: "Email Subject Line", letter: "E", category: "Email Marketing", def: "The preview text a recipient sees before opening an email — the single biggest lever on open rate." },
        { term: "Generative AI", letter: "G", category: "AI Marketing", def: "AI that creates new content — text, images, code — rather than just analysing existing data." },
        { term: "Hard Bounce", letter: "H", category: "Deliverability", def: "An email that permanently failed to deliver, usually because the address doesn't exist." },
        { term: "Integration", letter: "I", category: "CRM & Integrations", def: "A connection between Transpond and another tool that lets data flow between them automatically." },
        { term: "Landing Page", letter: "L", category: "Lead Generation", def: "A standalone web page designed around a single offer or action, usually linked from an ad or campaign." },
        { term: "Lead Generation", letter: "L", category: "Lead Generation", def: "The process of attracting and capturing interest from potential customers." },
        { term: "Lead Magnet", letter: "L", category: "Lead Generation", def: "A free resource — a guide, template, or discount — offered in exchange for someone's contact details." },
        { term: "Lead Nurturing", letter: "L", category: "Lead Generation", def: "Building a relationship with a potential customer over time, usually through targeted content and emails, until they're ready to buy." },
        { term: "Lead Scoring", letter: "L", category: "Contacts & Segmentation", def: "Ranking contacts by how likely they are to buy, based on their behaviour and profile." },
        { term: "List Building", letter: "L", category: "Contacts & Segmentation", def: "Growing your subscriber list through signup forms, lead magnets and other opt-in methods." },
        { term: "Marketing Automation", letter: "M", category: "Automation", def: "Using software to run repetitive marketing tasks automatically, at scale." },
        { term: "Marketing Funnel", letter: "M", category: "Automation", def: "The stages a person moves through on their way to becoming a customer." },
        { term: "Multichannel Marketing", letter: "M", category: "Social & Multichannel", def: "Reaching customers across more than one channel — email, SMS, social — as part of one coordinated strategy." },
        { term: "Order Confirmation Email", letter: "O", category: "Transactional Email", def: "A transactional email sent automatically after a purchase, confirming the details." },
        { term: "Password Reset Email", letter: "P", category: "Transactional Email", def: "A transactional email that lets a user securely reset a forgotten password." },
        { term: "Sender Reputation", letter: "S", category: "Deliverability", def: "A score inbox providers assign to your sending domain, based on your history of engagement, complaints and bounces." },
        { term: "SMS Compliance", letter: "S", category: "SMS Marketing", def: "Following the legal rules — consent, opt-out, timing — that govern sending marketing text messages." },
        { term: "SMS Consent", letter: "S", category: "SMS Marketing", def: "Explicit permission from someone to receive marketing texts, required before you can message them." },
        { term: "SMS Marketing", letter: "S", category: "SMS Marketing", def: "Sending marketing messages to subscribers by text, often for time-sensitive offers and reminders." },
        { term: "Social Media Automation", letter: "S", category: "Social & Multichannel", def: "Scheduling and publishing social posts automatically, rather than posting manually in the moment." },
        { term: "Social Media Calendar", letter: "S", category: "Social & Multichannel", def: "A planned schedule of what you'll post, and where, across your social channels." },
        { term: "Social Media Marketing", letter: "S", category: "Social & Multichannel", def: "Using social platforms to build awareness, engage an audience, and drive traffic or sales." },
        { term: "Soft Bounce", letter: "S", category: "Deliverability", def: "A temporary delivery failure, often due to a full inbox — the message may go through on a later attempt." },
        { term: "Spam Filter", letter: "S", category: "Deliverability", def: "A system inbox providers use to catch and redirect unwanted or suspicious email before it reaches the inbox." },
        { term: "Transactional Email", letter: "T", category: "Transactional Email", def: "A one-to-one message triggered by a specific action, like an order or password reset, rather than a marketing send." },
        { term: "Welcome Email", letter: "W", category: "Email Marketing", def: "The first email a new subscriber receives, setting expectations and often the highest-engagement send you'll make." },
      ],
    };
  },
  computed: {
    alphabet() {
      return "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
    },
    filteredTerms() {
      const query = this.appliedQuery.trim().toLowerCase();

      return this.terms.filter((term) => {
        const matchesCategory =
          !this.appliedCategory || term.category === this.appliedCategory;
        const matchesQuery =
          !query ||
          term.term.toLowerCase().includes(query) ||
          term.def.toLowerCase().includes(query);

        return matchesCategory && matchesQuery;
      });
    },
    groupedTerms() {
      const groups = {};

      this.filteredTerms.forEach((term) => {
        if (!groups[term.letter]) {
          groups[term.letter] = [];
        }
        groups[term.letter].push(term);
      });

      return Object.keys(groups)
        .sort()
        .map((letter) => ({ letter, terms: groups[letter] }));
    },
    activeLetters() {
      return new Set(this.groupedTerms.map((group) => group.letter));
    },
  },
  watch: {
    searchQuery() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.commitFilters();
      }, 140);
    },
  },
  mounted() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.pageShown = true;
      });
    });
  },
  beforeUnmount() {
    clearTimeout(this._searchTimer);
    clearTimeout(this._fadeTimer);
  },
  methods: {
    termHref(term) {
      const href = term && term.href;
      if (typeof href !== "string") return "";
      const trimmed = href.trim();
      if (!trimmed || trimmed === "#") return "";
      return trimmed;
    },
    setCategory(category) {
      this.activeCategory = this.activeCategory === category ? null : category;
      this.commitFilters();
    },
    commitFilters() {
      const unchanged =
        this.appliedQuery === this.searchQuery &&
        this.appliedCategory === this.activeCategory;

      if (!this.resultsFading && unchanged) return;

      const apply = () => {
        this.appliedQuery = this.searchQuery;
        this.appliedCategory = this.activeCategory;
        this.hasFiltered = true;
      };

      const reduce =
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (reduce) {
        apply();
        return;
      }

      clearTimeout(this._fadeTimer);
      this.resultsFading = true;
      this._fadeTimer = setTimeout(() => {
        apply();
        this.$nextTick(() => {
          this.resultsFading = false;
        });
      }, 180);
    },
  },
};
</script>
